import React, { useEffect, useState, useId } from 'react';

const COLOR_MAP = {
  excellent: {
    stroke: '#10b981',
    strokeEnd: '#06b6d4',
    glow: 'rgba(16, 185, 129, 0.20)',
    glowStrong: 'rgba(16, 185, 129, 0.35)',
    glowOuter: 'rgba(16, 185, 129, 0.08)',
    text: 'text-emerald-600',
    bg: 'bg-emerald-50',
    badge: 'bg-emerald-50/80 text-emerald-700 ring-1 ring-emerald-200/50 shadow-sm',
    badgeLabel: 'Excellent',
    badgeIcon: '\u2728',
  },
  high: {
    stroke: '#5AAFA7',
    strokeEnd: '#22d3ee',
    glow: 'rgba(90, 175, 167, 0.20)',
    glowStrong: 'rgba(90, 175, 167, 0.35)',
    glowOuter: 'rgba(90, 175, 167, 0.08)',
    text: 'text-primary-600',
    bg: 'bg-primary-50',
    badge: 'bg-primary-50/80 text-primary-700 ring-1 ring-primary-200/50 shadow-sm',
    badgeLabel: 'Good',
    badgeIcon: '\u2705',
  },
  mid: {
    stroke: '#f59e0b',
    strokeEnd: '#fb923c',
    glow: 'rgba(245, 158, 11, 0.20)',
    glowStrong: 'rgba(245, 158, 11, 0.35)',
    glowOuter: 'rgba(245, 158, 11, 0.08)',
    text: 'text-amber-600',
    bg: 'bg-amber-50',
    badge: 'bg-amber-50/80 text-amber-700 ring-1 ring-amber-200/50 shadow-sm',
    badgeLabel: 'Fair',
    badgeIcon: '\u26A0\uFE0F',
  },
  low: {
    stroke: '#f43f5e',
    strokeEnd: '#fb7185',
    glow: 'rgba(244, 63, 94, 0.20)',
    glowStrong: 'rgba(244, 63, 94, 0.35)',
    glowOuter: 'rgba(244, 63, 94, 0.08)',
    text: 'text-rose-600',
    bg: 'bg-rose-50',
    badge: 'bg-rose-50/80 text-rose-700 ring-1 ring-rose-200/50 shadow-sm',
    badgeLabel: 'Needs Work',
    badgeIcon: '\u{1F6A9}',
  },
};

const getColors = (score) => {
  if (score >= 90) return COLOR_MAP.excellent;
  if (score >= 70) return COLOR_MAP.high;
  if (score >= 50) return COLOR_MAP.mid;
  return COLOR_MAP.low;
};

const TICK_COUNT = 36;

const ScoreRing = ({
  score = 0,
  size = 144,
  strokeWidth = 10,
  label,
  subtitle,
  showBadge = false,
  animate = true,
  className = '',
}) => {
  const uid = useId();
  const gradientId = `ring-grad-${uid}`;
  const glowId = `ring-glow-${uid}`;
  const fillGradId = `ring-fill-${uid}`;
  const trackGradId = `ring-track-${uid}`;
  const [displayScore, setDisplayScore] = useState(animate ? 0 : score);
  const [done, setDone] = useState(!animate);
  const radius = (size - strokeWidth) / 2;
  const circumference = 2 * Math.PI * radius;
  const colors = getColors(score);

  const cx = size / 2;
  const cy = size / 2;
  const innerRadius = radius - strokeWidth / 2 - 2;
  const tickRadius = radius + strokeWidth / 2 + 2;
  const tickLength = 2.5;

  useEffect(() => {
    if (!animate) { setDisplayScore(score); setDone(true); return; }
    setDone(false);
    let start = null;
    const duration = 1000;
    const from = 0;
    const to = score;
    const step = (ts) => {
      if (!start) start = ts;
      const progress = Math.min((ts - start) / duration, 1);
      const ease = 1 - Math.pow(1 - progress, 3);
      setDisplayScore(Math.round(from + (to - from) * ease));
      if (progress < 1) {
        requestAnimationFrame(step);
      } else {
        setDone(true);
      }
    };
    const id = requestAnimationFrame(step);
    return () => cancelAnimationFrame(id);
  }, [score, animate]);

  const offset = circumference - (displayScore / 100) * circumference;
  const endAngleRad = (displayScore / 100) * 2 * Math.PI;
  const capX = cx + radius * Math.cos(endAngleRad);
  const capY = cy + radius * Math.sin(endAngleRad);

  return (
    <div className={`inline-flex flex-col items-center ${className}`}>
      <div className="relative" style={{ width: size, height: size }}>
        {done && (
          <div
            className="absolute inset-0 rounded-full animate-score-glow"
            style={{
              boxShadow: `0 0 0 1px ${colors.glowOuter}, 0 4px 24px -6px ${colors.glowStrong}, 0 8px 44px -8px ${colors.glowOuter}`,
            }}
          />
        )}

        <svg width={size} height={size} className="relative transform -rotate-90">
          <defs>
            <linearGradient id={gradientId} x1="0%" y1="0%" x2="100%" y2="0%">
              <stop offset="0%" stopColor={colors.stroke} />
              <stop offset="100%" stopColor={colors.strokeEnd} />
            </linearGradient>
            <radialGradient id={fillGradId} cx="50%" cy="50%" r="50%">
              <stop offset="0%" stopColor={colors.stroke} stopOpacity="0.06" />
              <stop offset="100%" stopColor={colors.stroke} stopOpacity="0.01" />
            </radialGradient>
            <radialGradient id={trackGradId} cx="50%" cy="50%" r="50%">
              <stop offset="60%" stopColor="#f9fafb" stopOpacity="0" />
              <stop offset="100%" stopColor="#f3f4f6" stopOpacity="0.5" />
            </radialGradient>
            <filter id={glowId} x="-10%" y="-10%" width="120%" height="120%">
              <feGaussianBlur stdDeviation="3" result="blur" />
              <feMerge>
                <feMergeNode in="blur" />
                <feMergeNode in="SourceGraphic" />
              </feMerge>
            </filter>
            <filter id={`${glowId}-cap`} x="-50%" y="-50%" width="200%" height="200%">
              <feGaussianBlur stdDeviation="3.5" result="blur" />
              <feMerge>
                <feMergeNode in="blur" />
                <feMergeNode in="SourceGraphic" />
              </feMerge>
            </filter>
          </defs>

          <circle cx={cx} cy={cy} r={innerRadius} fill={`url(#${fillGradId})`} />

          {Array.from({ length: TICK_COUNT }).map((_, i) => {
            const angle = (i / TICK_COUNT) * 360;
            const rad = (angle * Math.PI) / 180;
            const x1 = cx + tickRadius * Math.cos(rad);
            const y1 = cy + tickRadius * Math.sin(rad);
            const x2 = cx + (tickRadius - tickLength) * Math.cos(rad);
            const y2 = cy + (tickRadius - tickLength) * Math.sin(rad);
            const filled = (i / TICK_COUNT) * 100 <= displayScore;
            return (
              <line
                key={i}
                x1={x1} y1={y1} x2={x2} y2={y2}
                stroke={filled ? colors.stroke : '#e5e7eb'}
                strokeWidth={1}
                strokeLinecap="round"
                opacity={filled ? 0.55 : 0.15}
              />
            );
          })}

          <circle
            cx={cx} cy={cy} r={radius}
            fill="none"
            stroke="#f3f4f6"
            strokeWidth={strokeWidth}
          />

          <circle
            cx={cx} cy={cy} r={radius}
            fill="none"
            stroke={`url(#${gradientId})`}
            strokeWidth={strokeWidth}
            strokeLinecap="round"
            strokeDasharray={circumference}
            strokeDashoffset={offset}
            filter={done ? `url(#${glowId})` : undefined}
            style={{
              transition: animate ? 'none' : 'stroke-dashoffset 0.7s ease-out',
            }}
          />

          {displayScore > 2 && (
            <>
              <circle
                cx={capX}
                cy={capY}
                r={strokeWidth / 2 + 0.5}
                fill={colors.strokeEnd}
                opacity={done ? 0.9 : 0.6}
                filter={done ? `url(#${glowId}-cap)` : undefined}
              />
              <circle
                cx={capX}
                cy={capY}
                r={strokeWidth / 4}
                fill="white"
                opacity={done ? 0.35 : 0.15}
              />
            </>
          )}
        </svg>

        <div className="absolute inset-0 flex flex-col items-center justify-center">
          <span
            className={`font-bold tabular-nums leading-none tracking-tight ${colors.text}`}
            style={{
              fontSize: size * 0.28,
              textShadow: done ? `0 1px 10px ${colors.glowOuter}` : 'none',
              transition: 'transform 0.5s cubic-bezier(0.22, 1.8, 0.36, 1), text-shadow 0.6s ease',
              transform: done ? 'scale(1)' : 'scale(0.85)',
            }}
          >
            {displayScore}
          </span>
          {label && (
            <span
              className="font-medium text-gray-400/80 mt-0.5"
              style={{ fontSize: Math.max(size * 0.08, 10) }}
            >
              {label}
            </span>
          )}
        </div>
      </div>

      {showBadge && (
        <span
          className={`mt-3 inline-flex items-center gap-1.5 px-3.5 py-1 rounded-full text-xs font-semibold tracking-wide backdrop-blur-sm ${colors.badge}`}
          style={{
            opacity: done ? 1 : 0,
            transform: done ? 'translateY(0)' : 'translateY(6px)',
            transition: 'opacity 0.4s ease 0.2s, transform 0.4s cubic-bezier(0.22, 1.4, 0.36, 1) 0.2s',
          }}
        >
          <span className="text-[11px]">{colors.badgeIcon}</span>
          {colors.badgeLabel}
        </span>
      )}

      {subtitle && (
        <p className="mt-3 text-sm text-gray-500 text-center max-w-[220px] leading-relaxed">{subtitle}</p>
      )}
    </div>
  );
};

export default ScoreRing;
